import React, { useEffect } from 'react';
import Barcode from 'react-barcode';
import { Drawer } from '~/shared/components/Drawer';
import { useScrollLock } from '~/shared/hooks/useScrollLock';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ReceiptsCardDrawer.module.scss';
import Typography from '~/shared/components/Typography/Typography';
import type { ReceiptDetails } from '~/lib/data-contract/loyalty';

interface ReceiptsCardDrawerProps {
    open: boolean;
    onClose: () => void;
    receipt: ReceiptDetails;
    scrollLock?: boolean;
}

const ReceiptsCardDrawer: React.FC<ReceiptsCardDrawerProps> = ({
    open,
    onClose,
    receipt,
    scrollLock = true,
}) => {
    const { lock, unlock } = useScrollLock();
    const { translate } = useTranslation();

    // Apply scroll lock when drawer opens
    useEffect(() => {
        if (open && scrollLock) {
            lock();
        }

        return () => {
            if (scrollLock) {
                unlock();
            }
        };
    }, [open, scrollLock, lock, unlock]);

    // Format price with commas
    const formatPrice = (price: number): string => {
        return price.toLocaleString('da-DK', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    return (
        <Drawer open={open} onClose={onClose} isInfo={true}>
            <div className={styles.drawerContainer}>
                {/* Header with close button */}
                <div className={styles.header}>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={translate('loyalty.receipt.closeDrawer')}
                    >
                        ✕
                    </button>
                </div>

                <Typography
                    className={styles.heading}
                    as="h2"
                    variant="paragraph-XXL"
                    weight="medium"
                >
                    {translate('loyalty.receipt.title')}
                </Typography>

                {/* Store information */}
                <div className={styles.storeInfo}>
                    <Typography
                        className={styles.storeName}
                        as="h3"
                        variant="paragraph-M"
                        weight="medium"
                    >
                        {receipt.storeName}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {receipt.storeAddress}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {receipt.storePostal} {receipt.storeCity}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        Tlf.: {receipt.storePhone}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {receipt.storeEmail}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        CV€ nr. {receipt.storeCVR}
                    </Typography>
                </div>

                {/* Barcode */}
                <div className={styles.barcodeContainer}>
                    <div className={styles.barcode}>
                        {receipt.barcode ? (
                            <Barcode
                                value={receipt.barcode}
                                format="CODE128"
                                width={2}
                                height={60}
                                displayValue={true}
                                fontSize={14}
                                margin={10}
                                background="#ffffff"
                                lineColor="#000000"
                            />
                        ) : (
                            <p>{translate('loyalty.receipt.noBarcodeAvailable')}</p>
                        )}
                    </div>
                </div>

                {/* Receipt metadata */}
                <div className={styles.receiptMeta}>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {translate('common.barcodeNumber')}: {receipt.barcodeText}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {translate('common.clerk')}: {receipt.employee}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {translate('common.date')}: {receipt.date} {receipt.time}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" weight="regular">
                        {translate('common.transactionNumber')}: {receipt.transactionId}
                    </Typography>
                </div>

                {/* Receipt items */}
                <div className={styles.itemsContainer}>
                    {receipt.items.map((item, index) => (
                        <div key={index} className={styles.item}>
                            <div className={styles.itemDetails}>
                                <Typography as="p" variant="paragraph-XS" weight="regular">
                                    Vare nr. {item.itemNumber}
                                </Typography>
                                <Typography
                                    className={styles.itemDescription}
                                    as="p"
                                    variant="paragraph-XS"
                                    weight="medium"
                                >
                                    {item.description}
                                </Typography>
                                {item.quantity > 1 && (
                                    <Typography
                                        className={styles.itemQuantity}
                                        as="p"
                                        variant="paragraph-S"
                                        weight="regular"
                                    >
                                        {item.quantity} {translate('common.quantityUnitOf')}{' '}
                                        {formatPrice(item.price)}
                                    </Typography>
                                )}
                            </div>
                            <div className={styles.itemPrice}>
                                <Typography
                                    className={styles.itemQuantity}
                                    as="p"
                                    variant="paragraph-S"
                                    weight="regular"
                                >
                                    {formatPrice(item.total)}
                                </Typography>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Receipt totals */}
                <div className={styles.totals}>
                    <div className={styles.totalRow}>
                        <Typography
                            className={styles.totalLabel}
                            as="span"
                            variant="paragraph-S"
                            weight="medium"
                        >
                            I alt kr
                        </Typography>
                        <Typography
                            className={styles.totalValue}
                            as="span"
                            variant="paragraph-S"
                            weight="regular"
                        >
                            {formatPrice(receipt.totalAmount)}
                        </Typography>
                    </div>

                    <div className={styles.totalRow}>
                        <Typography
                            className={styles.totalLabel}
                            as="span"
                            variant="paragraph-S"
                            weight="regular"
                        >
                            {receipt.paymentMethod}
                        </Typography>
                        <Typography
                            className={styles.totalValue}
                            as="span"
                            variant="paragraph-S"
                            weight="regular"
                        >
                            -{formatPrice(receipt.totalAmount)}
                        </Typography>
                    </div>

                    <div className={styles.totalRow}>
                        <Typography
                            className={styles.totalLabel}
                            as="span"
                            variant="paragraph-S"
                            weight="regular"
                        >
                            Moms 25%
                        </Typography>
                        <Typography
                            className={styles.totalValue}
                            as="span"
                            variant="paragraph-S"
                            weight="regular"
                        >
                            {formatPrice(receipt.vat)}
                        </Typography>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default ReceiptsCardDrawer;
